'use client';

import { Suspense, useState, useEffect } from 'react';
import { publicApi } from '@/api/public';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Clock, MapPin } from 'lucide-react';
import type { Organisation } from '@/types/organisation';
import type { Job } from '@/types/jobs';
import { PoweredByFooter } from '@/components/layout/PoweredByFooter';
import { ExpandableContent } from '@/components/ui/expandable-content';

interface PageProps {
  params: Promise<{
    orgId: string;
  }>;
}

// Loading skeleton component
function OrganisationSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
          
          {/* Jobs skeleton */}
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border rounded-lg p-6 space-y-3">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="flex gap-2">
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main organization page component
function OrganisationPageContent({ orgId }: { orgId: string }) {
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    total: number;
    limit: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch organization and jobs data concurrently
        const [orgResult, jobsResult] = await Promise.allSettled([
          publicApi.getOrganisation(orgId),
          publicApi.listJobs(orgId, { page: 1, status: 'ACTIVE' })
        ]);

        // Process organization data
        if (orgResult.status === 'fulfilled') {
          const orgData = orgResult.value.organisation;
          if (!orgData || !orgData.id || !orgData.name) {
            throw new Error('Invalid organization data received');
          }
          setOrganisation(orgData);
        } else {
          console.error('Error fetching organization:', orgResult.reason);
          setError('Organization not found');
          return;
        }

        // Process jobs data
        if (jobsResult.status === 'fulfilled') {
          setJobs(jobsResult.value.jobs);
          setPagination(jobsResult.value.pagination);
        } else {
          console.error('Error fetching jobs:', jobsResult.reason);
          // Don't set error for jobs, just show empty state
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Something went wrong');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [orgId]);

  // Show loading state
  if (isLoading) {
    return <OrganisationSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-foreground">
            {error === 'Organization not found' ? 'Organization Not Found' : 'Something went wrong'}
          </h1>
          <p className="text-muted-foreground">
            {error === 'Organization not found' 
              ? "The organization you're looking for doesn't exist or has been removed."
              : "We encountered an error while loading this page. Please try again later."
            }
          </p>
          <div className="mt-6">
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!organisation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            We encountered an unexpected error while loading this organization. Please try again later.
          </p>
          <div className="mt-6">
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Organization Header */}
      <div className="bg-primary/5 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6 max-w-4xl mx-auto">
            <div className="w-24 h-24 overflow-hidden flex-shrink-0">
              {organisation.logoUrl ? (
                <Image
                  src={organisation.logoUrl}
                  alt={organisation.name}
                  width={96}
                  height={96}
                  className="w-24 h-24 object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xl font-semibold text-primary">
                  {organisation.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-foreground">{organisation.name}</h1>
              <p className="text-muted-foreground mt-1">
                {'location' in organisation ? (organisation.location as string) : 'Remote'}
              </p>
              {organisation.website && (
                <a
                  href={organisation.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline mt-2 inline-block"
                >
                  Visit Website
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-4xl mx-auto">
          {/* Description */}
          <div className="mb-8">
            <ExpandableContent maxLines={5}>
              <p className="text-lg text-secondary">{organisation.description}</p>
            </ExpandableContent>
          </div>

          {/* Jobs List */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-foreground">
                Open Positions {jobs.length ? `(${pagination?.total || jobs.length})` : ''}
              </h2>
            </div>

            {jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs
                  .filter(job => job.status === 'ACTIVE')
                  .map(job => (
                  <div
                    key={job.id}
                    className="group border border-secondary bg-card-background p-6 transition-colors hover:border-primary/50"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <Link
                          href={`/careers/${orgId}/jobs/${job.id}`}
                          className="text-xl font-semibold text-foreground hover:text-primary transition-colors"
                        >
                          {job.title}
                        </Link>
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            <span>{job.locationTypeLabel}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span>
                              {job.workExperience?.minimum || 0} -{' '}
                              {job.workExperience?.maximum || 1} years
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button asChild variant="outline">
                          <Link href={`/careers/${orgId}/jobs/${job.id}`}>
                            View Details
                          </Link>
                        </Button>
                        <Button asChild>
                          <a
                            href={`${process.env.NEXT_CANDIDATE_APP_URL}?jobId=${job.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Apply
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Basic Pagination - you can expand this if needed */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="flex justify-center mt-6 gap-2">
                    {Array.from({ length: pagination.totalPages }).map((_, i) => (
                      <Link
                        key={i}
                        href={`/careers/${orgId}?page=${i + 1}`}
                        className={`px-3 py-1 border rounded ${
                          pagination.currentPage === i + 1
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:bg-secondary/10'
                        }`}
                      >
                        {i + 1}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No open positions at the moment.
              </div>
            )}
          </div>
        </div>

        {/* Add space at the bottom for mobile views */}
        <div className="h-10 sm:h-6"></div>
      </div>

      {/* Fixed footer */}
      <div className="fixed bottom-0 left-0 right-0 w-full z-5 bg-background">
        <PoweredByFooter />
      </div>
    </div>
  );
}

export default async function OrganisationPage({ params }: PageProps) {
  const { orgId } = await params;
  return (
    <Suspense fallback={<OrganisationSkeleton />}>
      <OrganisationPageContent orgId={orgId} />
    </Suspense>
  );
}
